import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flame, X, ArrowRight } from 'lucide-react';
import { usePublicPromo } from '../hooks/usePublicPromo';

const DISMISS_KEY = 'dronemaps_summer_promo_dismissed_v1';

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const target = Date.parse(targetIso);
  if (Number.isNaN(target)) return null;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, expired: diff <= 0 };
}

/**
 * Locale-aware EUR formatter. Intl.NumberFormat handles the tricky
 * details (comma vs dot decimal, symbol placement, non-breaking space)
 * so we don't have to hand-code per-locale rules.
 */
export function formatEUR(value, lang) {
  try {
    return new Intl.NumberFormat(lang || 'fr', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value}€`;
  }
}

const Segment = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[42px]">
    <span className="font-mono text-xl md:text-2xl font-bold tabular-nums leading-none">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{label}</span>
  </div>
);

/**
 * Sticky top banner promoting the active public promo (currently SUMMER).
 * - Renders nothing when no promo is active, expired, or user dismissed it.
 * - Dismissal is per-browser (localStorage) — never expires until localStorage clears.
 * - Copy is fully i18n-ed via `landing.promo.*` keys; prices are formatted
 *   locale-aware through `Intl.NumberFormat`.
 */
export default function SummerPromoBanner({ to = '#pricing', variant = 'sticky' }) {
  const { t, i18n } = useTranslation();
  const { promo, loading } = usePublicPromo();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const countdown = useCountdown(promo?.expires_at);

  if (loading || !promo || dismissed) return null;
  if (countdown?.expired) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore quota */ }
    setDismissed(true);
  };

  const isSticky = variant === 'sticky';
  const soloPrice = promo.per_plan_price?.solo;
  const annualPrice = promo.per_plan_price?.solo_annual;
  const soloTxt = typeof soloPrice === 'number' ? formatEUR(soloPrice, i18n.language) : '';
  const annualTxt = typeof annualPrice === 'number' ? formatEUR(annualPrice, i18n.language) : '';
  const labels = t('landing.promo.labels', { returnObjects: true }) || { days: 'j', hours: 'h', minutes: 'min', seconds: 's' };

  return (
    <div
      data-testid="summer-promo-banner"
      className={`${isSticky ? 'sticky top-20 z-40' : 'relative'} w-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-2.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2 font-semibold text-sm md:text-base">
          <Flame className="w-5 h-5 shrink-0" />
          <span className="uppercase tracking-wide">{t('landing.promo.kicker')}</span>
          <span className="hidden sm:inline text-white/90">·</span>
          <span className="hidden sm:inline text-white/90 font-normal">
            {t('landing.promo.details', { solo: soloTxt, annual: annualTxt })}
          </span>
        </div>

        {countdown && (
          <div className="flex items-center gap-2" data-testid="summer-promo-countdown">
            <span className="hidden md:inline text-xs uppercase tracking-wider opacity-80">
              {promo.recurring_weekly ? t('landing.promo.endsInWeekly') : t('landing.promo.endsIn')}
            </span>
            <div className="flex items-center gap-1.5">
              <Segment value={countdown.days} label={labels.days} />
              <span className="opacity-60">:</span>
              <Segment value={countdown.hours} label={labels.hours} />
              <span className="opacity-60">:</span>
              <Segment value={countdown.minutes} label={labels.minutes} />
              <span className="opacity-60">:</span>
              <Segment value={countdown.seconds} label={labels.seconds} />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {to.startsWith('#') ? (
            <a
              href={to}
              className="inline-flex items-center gap-1.5 bg-white text-orange-600 hover:bg-orange-50 font-bold text-sm px-4 py-1.5 rounded-full transition-colors shadow-sm"
              data-testid="summer-promo-cta"
            >
              {t('landing.promo.cta')}
              <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <Link
              to={`${to}${to.includes('?') ? '&' : '?'}promo=${promo.code}`}
              className="inline-flex items-center gap-1.5 bg-white text-orange-600 hover:bg-orange-50 font-bold text-sm px-4 py-1.5 rounded-full transition-colors shadow-sm"
              data-testid="summer-promo-cta"
            >
              {t('landing.promo.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('landing.promo.dismiss')}
            data-testid="summer-promo-dismiss"
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
